import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

import { SaveState } from '../types/ForrmType';
import { useUpdateCompaniesMutation } from '../store/modules/companies';
import { urlRegex } from '../constants/regex';

interface CompanyFormData {
  Name: string;
  Url?: string;
  Logo?: Blob | null;
}

const useUpdateCompany = (
  companyId: number,
  companyName: string,
  companyUrl: string | null,
  setModal: React.Dispatch<React.SetStateAction<boolean>>
) => {
  const [t] = useTranslation('global');
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isDirty },
  } = useForm<CompanyFormData>({
    defaultValues: {
      Name: companyName,
      Url: companyUrl || '',
    },
  });

  const [showCancel, setShowCancel] = useState(false);
  const [saveState, setSaveState] = useState<SaveState>(SaveState.IDLE);
  const [updateCompany] = useUpdateCompaniesMutation();
  const [logoFile, setLogoFile] = useState<Blob | null>(null);

  const name = register('Name', {
    required: `${t('CompaniesTable.ErrorName')}`,
  });

  const url = register('Url', {
    pattern: {
      value: urlRegex,
      message: `${t('CompaniesTable.ErrorUrl')}`,
    },
  });

  const onSubmit = async (data: CompanyFormData) => {
    setSaveState(SaveState.PENDING);

    const formData = new FormData();
    formData.append('Name', data.Name.trim());

    if (data.Url) {
      formData.append('Url', data.Url.trim());
    }

    if (logoFile) {
      const logoFileObj = new File([logoFile], 'company-logo.jpg', {
        type: logoFile.type || 'image/jpeg',
      });
      formData.append('Logo', logoFileObj);
    }

    await updateCompany({
      companyId,
      formData,
    });
    setSaveState(SaveState.IDLE);
    setModal(false);
  };

  const handleCancel = () => {
    setShowCancel(true);
  };
  const handleCancelModal = (isCancel: boolean) => {
    if (isCancel) {
      setModal(false);
    }
    setShowCancel(false);
  };

  const handleLogoChange = (blob: Blob | null) => {
    setLogoFile(blob);
    setValue('Logo', blob, { shouldDirty: true });
  };

  const isDisabled = saveState === SaveState.PENDING || !isDirty;

  return {
    isDisabled,
    handleSubmit,
    name,
    url,
    t,
    handleCancel,
    handleCancelModal,
    errors,
    onSubmit,
    saveState,
    showCancel,
    logoFile,
    handleLogoChange,
  };
};

export default useUpdateCompany;
